/**
 * The column layouts of the client-side CSV exports, kept out of the screens so the
 * two report widths (a roster of people, a grid of days) each have exactly one
 * definition of what a row is.
 *
 * Durations go out as MINUTES, not as "8 ч 30 мин". A spreadsheet is opened to be
 * summed and averaged, and a localized duration string is text — the first thing
 * anyone would have to do with this file is undo the formatting. The header says the
 * unit, and that is enough.
 */
import type {
  AttendanceDayRow,
  AttendanceEmployeeSummary,
} from '@/entities/attendance/types';
import {attendanceRate} from './aggregate';
import {buildCsv} from './csv';

export type Translate = (key: string, options?: Record<string, unknown>) => string;

/** One row per person: the summary table, exactly as filtered and sorted on screen. */
export function employeesCsv(
  rows: readonly AttendanceEmployeeSummary[],
  t: Translate,
): Blob {
  const headers = [
    t('reports.col.employee'),
    t('reports.col.number'),
    t('reports.col.position'),
    t('reports.csv.scheduledDays'),
    t('reports.col.present'),
    t('reports.col.absent'),
    t('attendance.flag.onTime'),
    t('reports.col.late'),
    t('attendance.flag.earlyArrival'),
    t('attendance.flag.earlyLeave'),
    t('reports.col.overtime'),
    t('attendance.flag.unscheduled'),
    t('attendance.flag.incomplete'),
    t('reports.csv.workedMinutes'),
    t('reports.col.rate'),
  ];

  const body = rows.map(row => {
    const s = row.summary;
    const rate = attendanceRate(s);
    return [
      row.full_name,
      row.employee_number,
      row.position,
      s.scheduled_days,
      s.present_days,
      s.absent_days,
      s.on_time_days,
      s.late_days,
      s.early_arrival_days,
      s.early_leave_days,
      s.overtime_days,
      s.unscheduled_days,
      s.incomplete_days,
      s.total_worked_minutes,
      rate == null ? '' : Math.round(rate * 100),
    ];
  });

  return buildCsv(headers, body);
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Epoch millis → local "HH:MM", or empty. Same rule as shared/lib/format's clock. */
function clock(millis: number | null | undefined): string {
  if (millis == null) {
    return '';
  }
  const d = new Date(millis);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/**
 * One row per person per day — the detailed export, available whenever the report was
 * fetched with `include_days`.
 *
 * `flags` go out as a space-separated list of the RAW server codes, not the translated
 * labels: this column is the one a filter or a pivot table is built on, and a code
 * that survives a change of interface language is worth more here than a pretty word.
 */
export function daysCsv(rows: readonly AttendanceDayRow[], t: Translate): Blob {
  const headers = [
    t('reports.csv.date'),
    t('reports.col.employee'),
    t('reports.col.number'),
    t('reports.csv.scheduled'),
    t('reports.csv.arrival'),
    t('reports.csv.departure'),
    t('reports.csv.arrivalDevice'),
    t('reports.csv.departureDevice'),
    t('reports.csv.events'),
    t('reports.csv.workedMinutes'),
    t('reports.csv.intervalMinutes'),
    t('reports.csv.flags'),
  ];

  const body = rows.map(row => [
    row.date,
    row.full_name,
    row.employee_number,
    row.scheduled ? 1 : 0,
    clock(row.arrival_time_millis),
    clock(row.departure_time_millis),
    row.arrival_device_name ?? '',
    row.departure_device_name ?? '',
    row.event_count,
    row.worked_minutes ?? '',
    row.interval_minutes ?? '',
    row.flags.join(' '),
  ]);

  return buildCsv(headers, body);
}
